import React, { useMemo, useState } from "react";
import {
  Title,
  Select,
  GoBack,
  TextArea,
  MyMessage,
  PhoneInput,
  InputField,
  DashboardIntro,
  MyActionButton,
  MyButton,
} from "../../home/component/Component";
import { codeOk } from "../../../middlewares/index";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { getOneNightlife } from "../../../middlewares/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../middlewares/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../home/component/Pricing";
import GenerateDescription from "../utils/GenerateDescription";

/* ---------- Helpers ---------- */
const slugify = (str = "") =>
  str
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

const dayOptions = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "annual",
];

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const nightlife = getOneNightlife() || {};
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  /* ---------- UI helpers ---------- */
  const container = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const sectionPad = "p-4 sm:p-5";

  /* ---------- Alerts ---------- */
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  /* ---------- Fields ---------- */
  const nightlife_id = nightlife?.nightlife_id;

  const [thumbnail, setThumbnail] = useState(
    nightlife?.thumbnail || defaultImage
  );
  const [thumbnailId, setThumbnailId] = useState(nightlife?.image_cdn_id || "");

  const [title, setTitle] = useState(nightlife?.title || "");
  const [venue, setVenue] = useState(nightlife?.venue || "");

  const [startDate, setStartDate] = useState(nightlife?.start_date || "");
  const [endDate, setEndDate] = useState(nightlife?.end_date || "");
  const [startTime, setStartTime] = useState(nightlife?.start_time || "");
  const [endTime, setEndTime] = useState(nightlife?.end_time || "");

  const [email, setEmail] = useState(nightlife?.email_address || "");
  const [payLink, setPayLink] = useState(nightlife?.pay_link);
  const [phoneNumber, setPhoneNumber] = useState(nightlife?.phone_number || "");
  const [whatsapp, setWhatsapp] = useState(nightlife?.whatsapp || "none");
  const [facebook, setFacebook] = useState(nightlife?.facebook || "none");
  const [twitter, setTwitter] = useState(nightlife?.twitter || "none");
  const [instagram, setInstagram] = useState(nightlife?.instagram || "none");
  const [youtube, setYoutube] = useState(nightlife?.youtube || "none");

  const [hashtags, setHashtags] = useState(nightlife?.hashtag || "");
  const [googleMapCode, setGoogleMapCode] = useState(
    nightlife?.google_map_code || "none"
  );
  const [latitude, setLatitude] = useState(nightlife?.latitude || "none");
  const [longitude, setLongitude] = useState(nightlife?.longitude || "none");
  const [formattedAddress, setFormattedAddress] = useState(
    nightlife?.formatted_address || ""
  );

  const [featured, setFeatured] = useState(nightlife?.featured ?? "no");
  const [promoted, setPromoted] = useState(
    nightlife?.promoted ? nightlife.promoted : "no"
  );
  const [nightlifeStatus, setNightlifeStatus] = useState(
    nightlife?.status ?? 1
  );
  const [description, setDescription] = useState(nightlife?.description || "");
  const [repeatEvery, setRepeatEvery] = useState(nightlife?.repeat_every || "");
  const [expo, setExpo] = useState(nightlife?.expo ?? 0);
  const [pricing, setPricing] = useState(nightlife?.Pricings || []);

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const nightlifeRoutes = "/data/nightlife";

  /* ---------- Messages ---------- */
  const responses = (isError, msg) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg || "");
  };

  /* ---------- Repeat toggles ---------- */
  const isDaySelected = (val) =>
    (repeatEvery || "").toString().split("|").filter(Boolean).includes(val);

  const toggleRepeat = (val) => {
    const parts = repeatEvery ? repeatEvery.split("|").filter(Boolean) : [];
    const exists = parts.includes(val);
    const next = exists ? parts.filter((x) => x !== val) : [...parts, val];
    setRepeatEvery(next.join("|"));
  };

  /* ---------- Validation ---------- */
  const canSubmit = useMemo(() => {
    if (!title || !thumbnail || !thumbnailId) return false;
    if (startDate && endDate) {
      const sd = new Date(`${startDate}T${startTime || "00:00"}`);
      const ed = new Date(`${endDate}T${endTime || "23:59"}`);
      if (sd > ed) return false;
    }
    return true;
  }, [title, thumbnail, thumbnailId, startDate, endDate, startTime, endTime]);

  /* ---------- Submit ---------- */
  const updateOneNightlifeHandler = async (e) => {
    e.preventDefault();

    if (!thumbnail || !thumbnailId) {
      responses(true, "Profile image is required.");
      return;
    }
    if (!title) {
      responses(true, "Title is required.");
      return;
    }
    if (startDate && endDate) {
      const sd = new Date(`${startDate}T${startTime || "00:00"}`);
      const ed = new Date(`${endDate}T${endTime || "23:59"}`);
      if (sd > ed) {
        responses(true, "Start date/time must be before end date/time.");
        return;
      }
    }

    setLoading(true);
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ?? nightlife?.country_id,
      first_level_id: first_level_id || 0,
      second_level_id: second_level_id || 0,
      third_level_id: third_level_id || 0,
      fourth_level_id: fourth_level_id || 0,

      title,
      slug: title ? slugify(title) : null,
      description,

      venue,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,

      thumbnail: thumbnail || defaultImage,
      image_cdn_id: thumbnailId || "",

      phone_number: phoneNumber || null,
      email_address: email || null,
      pay_link: payLink,
      whatsapp,
      facebook,
      twitter,
      instagram,
      youtube,

      hashtag: hashtags,
      audio_adv: "none",

      google_map_code: googleMapCode,
      latitude,
      longitude,
      formatted_address: formattedAddress || null,
      country_short: "none",

      status: nightlifeStatus,
      featured,
      promoted,
      repeat_every: repeatEvery,
      expo,
      pricing,
    };

    const { message, status } = await updateOneContent({
      data_id: nightlife_id,
      data,
      content: _TYPES_.NIGHTLIFE,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk || status === 200)
      responses(false, message || "Updated.");
    else responses(true, message || "Failed to update.");
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={`${container} py-4 lg:py-6`}>
        <div className="mb-3">
          <BreadCrumbComponent />
        </div>

        <div className="flex items-center justify-between mb-2">
          <GoBack props={{ route: nightlifeRoutes }} />
        </div>

        {showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null}

        <div className="mb-4">
          <DashboardIntro
            props={{ intro: `Edit Nightlife`, name: entry_label }}
          />
        </div>

        {/* Alerts */}
        <div className={`${card} ${sectionPad} mb-4`}>
          <MyMessage
            error={error}
            message={message}
            success={success}
            onExpired={() => {
              setError(false);
              setSuccess(false);
            }}
            onClick={() => {
              setError(false);
              setSuccess(false);
              setErrorMessage("");
            }}
          />
        </div>

        <form onSubmit={updateOneNightlifeHandler}>
          {/* Media + Basic Details */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-full md:w-1/2">
                <p className="text-xs font-semibold text-slate-600 mb-2">
                  Profile Image
                </p>
                <ImageKitUpload
                  thumbnail={thumbnail}
                  thumbnailId={thumbnailId}
                  setThumbnail={setThumbnail}
                  setThumbnailId={setThumbnailId}
                  folder={image_kit_folder(country_name, FOLDERS.NIGHTLIFE)}
                />
                <p className="text-xs text-slate-500 mt-2">
                  Recommended: square image, clear logo or venue cover.
                </p>
              </div>

              <div className="w-full md:w-1/2 space-y-3">
                <Title props={{ title: "Nightlife Details" }} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    props={{
                      id: "title",
                      title: "Title",
                      type: "text",
                      value: title,
                      placeholder: "e.g., Full Moon Party",
                      onChange: (e) => {
                        setTitle(e.target.value);
                        setError(false);
                        setSuccess(false);
                      },
                    }}
                  />
                  <InputField
                    props={{
                      id: "venue",
                      title: "Venue",
                      type: "text",
                      value: venue,
                      placeholder: "e.g., Ocean Club",
                      onChange: (e) => {
                        setVenue(e.target.value);
                        setError(false);
                        setSuccess(false);
                      },
                    }}
                  />
                  <InputField
                    props={{
                      id: "formatted_address",
                      title: "Formatted Address",
                      type: "address",
                      value: formattedAddress,
                      placeholder: "Street, City, Country",
                      onChange: (e) => {
                        setFormattedAddress(e.target.value);
                        setError(false);
                        setSuccess(false);
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Date & Time */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Date & Time" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <InputField
                props={{
                  id: "start_date",
                  title: "Start Date",
                  type: "date",
                  value: startDate,
                  onChange: (e) => {
                    setStartDate(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "end_date",
                  title: "End Date",
                  type: "date",
                  value: endDate,
                  onChange: (e) => {
                    setEndDate(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "start_time",
                  title: "Start Time",
                  type: "time",
                  value: startTime,
                  onChange: (e) => {
                    setStartTime(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "end_time",
                  title: "End Time",
                  type: "time",
                  value: endTime,
                  onChange: (e) => {
                    setEndTime(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
            </div>

            <div className="mt-5">
              <p className="text-sm font-medium text-slate-700">Repeat Every</p>
              <div className="flex flex-wrap gap-3 mt-2">
                {dayOptions.map((val) => {
                  const active = isDaySelected(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => toggleRepeat(val)}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 border text-sm capitalize transition ${
                        active
                          ? "bg-blue-50 border-blue-200 text-blue-700"
                          : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {active ? (
                        <MdCheckBox className="text-lg" />
                      ) : (
                        <MdCheckBoxOutlineBlank className="text-lg" />
                      )}
                      {val}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Pricing" }} />
            <Pricing pricing={pricing} setPricing={setPricing} />
          </div>

          {/* Contact */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Contact Info" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div className="flex items-center">
                <PhoneInput
                  setPhone={setPhoneNumber}
                  phone={phoneNumber}
                  required={false}
                />
              </div>
              <InputField
                props={{
                  id: "email",
                  title: "Email Address",
                  type: "email",
                  value: email,
                  onChange: (e) => {
                    setEmail(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "pay-link",
                  title: "Pay Link (Optional)",
                  type: "text",
                  value: payLink ? payLink : null,
                  onChange: (e) => {
                    setPayLink(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>
          </div>

          {/* Social */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Social Contact" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <InputField
                props={{
                  id: "whatsapp",
                  title: "Whatsapp",
                  type: "text",
                  value: whatsapp,
                  onChange: (e) => {
                    setWhatsapp(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "facebook",
                  title: "Facebook",
                  type: "text",
                  value: facebook,
                  onChange: (e) => {
                    setFacebook(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "twitter",
                  title: "Twitter",
                  type: "text",
                  value: twitter,
                  onChange: (e) => {
                    setTwitter(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "instagram",
                  title: "Instagram",
                  type: "text",
                  value: instagram,
                  onChange: (e) => {
                    setInstagram(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "youtube",
                  title: "YouTube",
                  type: "text",
                  value: youtube,
                  onChange: (e) => {
                    setYoutube(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
            </div>
          </div>

          {/* Geo */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "Geo Position" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <InputField
                props={{
                  id: "google_map_code",
                  title: "Google Map Code",
                  type: "text",
                  value: googleMapCode,
                  onChange: (e) => {
                    setGoogleMapCode(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "latitude",
                  title: "Latitude",
                  type: "text",
                  value: latitude,
                  onChange: (e) => {
                    setLatitude(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <InputField
                props={{
                  id: "longitude",
                  title: "Longitude",
                  type: "text",
                  value: longitude,
                  onChange: (e) => {
                    setLongitude(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
            </div>
          </div>

          {/* Settings */}
          <div className={`${card} ${sectionPad} mb-4`}>
            <Title props={{ title: "More Settings" }} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <Select
                props={{
                  id: "nightlife_status",
                  title: "Status",
                  option: ["visible", "invisible"],
                  preselect:
                    nightlifeStatus !== null
                      ? [
                          {
                            name:
                              nightlifeStatus === 1 ? "visible" : "invisible",
                          },
                        ]
                      : null,
                  onChange: (e) => {
                    setNightlifeStatus(e.target.selectedIndex === 0 ? 1 : 0);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <Select
                props={{
                  id: "featured",
                  title: "Featured",
                  option: ["no", "yes"],
                  preselect: featured !== null ? [{ name: featured }] : null,
                  onChange: (e) => {
                    setFeatured(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <Select
                props={{
                  id: "promoted",
                  title: "Promoted",
                  option: ["no", "yes"],
                  preselect: promoted !== null ? [{ name: promoted }] : null,
                  onChange: (e) => {
                    setPromoted(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
              <Select
                props={{
                  id: "gallery",
                  title: "Show in Gallery",
                  option: ["no", "yes"],
                  preselect:
                    expo !== null
                      ? [{ name: expo === 0 ? "no" : "yes" }]
                      : null,
                  onChange: (e) => {
                    setExpo(e.target.value === "no" ? 0 : 1);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
            </div>
          </div>

          {/* Description & Tags */}
          <div className={`${card} ${sectionPad} mb-6`}>
            <div className="flex items-center justify-between mb-3">
              <Title props={{ title: "Description" }} />
              <MyButton
                onClick={() => setShowDescriptionModal(true)}
                className="bg-blue-700 hover:bg-blue-600 text-white px-3 py-2 rounded-lg"
              >
                Generate Description
              </MyButton>
            </div>

            <TextArea
              props={{
                id: "description",
                title: "Description",
                type: "text",
                value: description,
                onChange: (e) => {
                  setDescription(e.target.value);
                  setError(false);
                  setSuccess(false);
                },
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <InputField
                props={{
                  id: "hashtag",
                  title: "Tags (comma separated)",
                  type: "text",
                  value: hashtags,
                  placeholder: "music, dance, rooftop",
                  onChange: (e) => {
                    setHashtags(e.target.value);
                    setError(false);
                    setSuccess(false);
                  },
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end">
            <MyActionButton
              loading={loading}
              title="Save Changes"
              onClick={() => {
                setError(false);
                setSuccess(false);
                setErrorMessage("");
              }}
              // Submission is handled by the form onSubmit
            />
          </div>

          {/* Hidden submit preserves Enter-to-submit UX even if MyActionButton isn't type=submit */}
          <button
            type="submit"
            className="hidden"
            disabled={!canSubmit || loading}
          />
        </form>
      </div>
    </div>
  );
};

export default Edit;
